import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ClientTable from '../../../components/tables/ClientTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useClientApi } from '../../../hooks/useClientApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ClientDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const ClientList: React.FC = () => {
  const { search, remove, isLoading, error } = useClientApi();
  const [items, setItems] = useState<ClientDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Clients', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Clients:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: ClientDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting Client:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/clients"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
    >
      <Card
        title="Manage Clients"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/clients/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="companyName"
              label="Search by Company Name"
              value={searchFilters.companyName || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, companyName: e.target.value })}
              placeholder="Search by Company Name"
            />
            <FormField
              id="contactPerson"
              label="Search by Contact Person"
              value={searchFilters.contactPerson || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, contactPerson: e.target.value })}
              placeholder="Search by Contact Person"
            />
            <FormField
              id="number"
              label="Search by Number"
              value={searchFilters.number || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, number: e.target.value })}
              placeholder="Search by Number"
            />
            <FormField
              id="email"
              label="Search by Email"
              value={searchFilters.email || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, email: e.target.value })}
              placeholder="Search by Email"
            />

          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ClientTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/clients/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ClientList;